// Magic-byte format detection for uploaded media.
//
// Both signals we used to trust lie in practice: iOS shares a HEIC named
// ".jpg", and several browsers report an empty MIME type for HEIC/TIFF. A lie
// sends the file down the wrong branch of the compression pipeline (canvas
// instead of heic2any), where it fails to decode and the upload dead-ends.
// The header bytes are the only signal that cannot be renamed away.

export type SniffedFormat =
  | 'jpeg' | 'png' | 'gif' | 'bmp' | 'webp' | 'tiff' | 'heic' | 'avif';

/** How much of the header we read - enough for the ISO-BMFF compatible-brand list. */
export const SNIFF_BYTES = 32;

function ascii(bytes: Uint8Array, start: number, end: number): string {
  let out = '';
  for (let i = start; i < end && i < bytes.length; i++) out += String.fromCharCode(bytes[i]);
  return out;
}

function startsWith(bytes: Uint8Array, signature: number[]): boolean {
  return bytes.length >= signature.length && signature.every((b, i) => bytes[i] === b);
}

// HEIF-family brands no browser canvas decodes (heic2any handles them).
const HEIC_BRANDS = ['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'hevm', 'hevs', 'mif1', 'msf1'];
// AVIF shares the ISO-BMFF container but IS canvas-decodable in current browsers.
const AVIF_BRANDS = ['avif', 'avis'];

/**
 * Identify an image from its leading header bytes, or null when the header
 * matches nothing we know (callers then fall back to MIME/extension).
 */
export function sniffFormatFromBytes(bytes: Uint8Array): SniffedFormat | null {
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'jpeg';
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'png';
  if (ascii(bytes, 0, 4) === 'GIF8') return 'gif';
  if (ascii(bytes, 0, 2) === 'BM') return 'bmp';
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') return 'webp';
  // TIFF byte order marks: little-endian "II*\0" and big-endian "MM\0*".
  if (startsWith(bytes, [0x49, 0x49, 0x2a, 0x00]) || startsWith(bytes, [0x4d, 0x4d, 0x00, 0x2a])) {
    return 'tiff';
  }
  if (ascii(bytes, 4, 8) === 'ftyp') {
    // Layout: [size][ftyp][major brand][minor version][compatible brands...].
    // The minor version at 12..16 is a number, not a brand, so it is skipped.
    const brands = [ascii(bytes, 8, 12).toLowerCase()];
    for (let i = 16; i + 4 <= bytes.length; i += 4) brands.push(ascii(bytes, i, i + 4).toLowerCase());
    // AVIF is checked first: an AVIF may carry the generic 'mif1' major brand
    // with 'avif' among its compatible brands, and it must not go to heic2any.
    if (brands.some((b) => AVIF_BRANDS.includes(b))) return 'avif';
    if (brands.some((b) => HEIC_BRANDS.includes(b))) return 'heic';
  }
  return null;
}

/** Read a file's leading bytes and identify its real format. Never throws. */
export async function sniffFormat(file: Blob): Promise<SniffedFormat | null> {
  try {
    const buffer = await file.slice(0, SNIFF_BYTES).arrayBuffer();
    return sniffFormatFromBytes(new Uint8Array(buffer));
  } catch {
    return null; // an unreadable header is simply "unknown"
  }
}
